import Vue from "vue";
import VueRouter from "vue-router";

import firebase from "@/firebase/firebaseInit";
import C from "@/c";
import { isAtelierUser } from "@/atelier"; // ATELIER — temporary, see src/atelier.js

Vue.use(VueRouter);

const auth = firebase.auth;
const db = firebase.db;

const routes = [
  {
    name: "Login",
    path: "/login",
    component: () => import("@/views/Login.vue"),
    meta: {
      guestOnly: true,
    },
  },
  {
    name: "Register",
    path: "/register",
    component: () => import("@/views/Register.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: "/",
    component: () => import("@/views/Layout.vue"),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        name: "Dashboard",
        path: "",
        component: () => import("@/components/ConfiguratorSelect.vue"),
      },
      {
        name: "ConfiguratorSelect",
        path: "/select",
        component: () => import("@/components/ConfiguratorSelect.vue"),
      },
      {
        name: "Configurator",
        path: "/configurator",
        component: () => import("@/components/ConfiguratorMgd.vue"),
        meta: {
          notForAtelier: true, // ATELIER — client is sent to Atelier instead
        },
      },
      {
        name: "ConfiguratorSimple",
        path: "/configurator/simple",
        component: () => import("@/components/ConfiguratorMgdSimple.vue"),
        meta: {
          notForAtelier: true, // ATELIER — client is sent to Atelier instead
        },
      },
      // ATELIER — temporary one-client configurator, see src/atelier.js
      {
        name: "ConfiguratorAtelier",
        path: "/configurator/atelier",
        component: () => import("@/components/ConfiguratorMgdAtelier.vue"),
        meta: {
          requiresAtelier: true,
        },
      },
      {
        name: "DashboardUser",
        path: "/dashboard",
        component: () => import("@/components/ConfiguratorMgd.vue"),
        meta: {
          notForAtelier: true, // ATELIER — client is sent to Atelier instead
        },
      },
      {
        name: "DashboardAdmin",
        path: "/admin/dashboard",
        component: () => import("@/components/Dashboard.vue"),
        meta: {
          requiresAdmin: true,
        },
      },
      {
        name: "Users",
        path: "/admin/users",
        component: () => import("@/components/Users.vue"),
        meta: {
          requiresAdmin: true,
        },
      },
      {
        name: "Configurations",
        path: "/admin/configurations",
        component: () => import("@/components/Configurations.vue"),
        meta: {
          requiresAdmin: true,
        },
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  routes,
});

function getCurrentUser() {
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

function checkIsAdmin(user) {
  if (!user) return Promise.resolve(false);

  return db
    .collection(C.COLLECTION.USERS)
    .doc(user.uid)
    .get()
    .then((doc) => {
      if (!doc.exists) return false;

      const userDoc = doc.data();

      return userDoc.userType === "admin" && userDoc.active === true;
    })
    .catch(() => {
      return false;
    });
}

router.beforeEach((to, from, next) => {
  getCurrentUser().then((currentUser) => {
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);
    const guestOnly = to.matched.some((record) => record.meta.guestOnly);
    const requiresAtelier = to.matched.some((record) => record.meta.requiresAtelier); // ATELIER
    const notForAtelier = to.matched.some((record) => record.meta.notForAtelier);     // ATELIER

    if (requiresAuth && !currentUser) {
      next({ name: "Login" });
      return;
    }

    // ATELIER — only the listed client; everyone else back to the model picker
    if (requiresAtelier && !isAtelierUser(currentUser)) {
      next({ name: "ConfiguratorSelect" });
      return;
    }

    // ATELIER — the client only uses Atelier: Serie 1 / Classic send them there
    if (notForAtelier && isAtelierUser(currentUser)) {
      next({ name: "ConfiguratorAtelier" });
      return;
    }

    if (guestOnly && currentUser) {
      next({ name: "Dashboard" });
      return;
    }

    if (requiresAdmin) {
      checkIsAdmin(currentUser).then((admin) => {
        if (!admin) {
          next({ name: "Dashboard" });
          return;
        }

        next();
      });

      return;
    }

    next();
  });
});

export default router;
